/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */
// eslint-disable-next-line react-hooks/exhaustive-deps
import { getReportImages, ReportPreviewData } from '@/services/report-library';
import { DownOutlined, LoadingOutlined } from '@ant-design/icons';
import {
  Alert, Button, Spin,
} from 'antd';
import React, {
  useState, useEffect, useRef, memo, FC,
  createRef,
} from 'react';
import { useLocation } from 'react-router-dom';
import { getZoneReportImages } from '@/services/single-product/home';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { PhotoSlider } from 'react-photo-view';
import styles from '../index.module.less';

// 类型定义
interface ImageType {
  id: string;
  url: string;
}

interface Props {
  data: ReportPreviewData,
  anchor: number,
  productId?: number,
  openid: string|number,
  trigger?: boolean, // 跳转触发器，为了点击到相同的索引也可跳转
  toJumpIdx?: (idx: number) => void, // 滚动触发对应目录展开
  showScroll?: (show: boolean) => void, // 是否显示滚动条
}

interface ImageSize {
  width: number;
  height: number;
}

interface ImageItemProps {
  image: ImageType;
  placeholderSize: ImageSize;
  isLoaded: boolean;
  index: number;
  notFixedSize?: boolean; // 是否不固定图片大小 (初始化的五张图片不设置固定大小，让图片自动适应，以便于在不同屏幕宽度下计算出图片容器的宽高)
  sendFirstImageSize?: (size: ImageSize) => void; // 用于发送第一个图片的大小
  onClick: () => void;
}

// 图片项组件
const ImageItem = memo<ImageItemProps>(({
  image,
  placeholderSize,
  index,
  isLoaded,
  notFixedSize,
  onClick,
  sendFirstImageSize,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const loadedImage = (img: HTMLImageElement) => {
    if (sendFirstImageSize && index === 0) {
      sendFirstImageSize({
        width: img.offsetWidth,
        height: img.offsetHeight,
      });
    }
  };

  const resizeImage = () => {
    if (sendFirstImageSize && index === 0 && ref.current) {
      sendFirstImageSize({
        width: ref.current.offsetWidth,
        height: ref.current.offsetHeight,
      });
    }
  };

  useEffect(() => {
    window.addEventListener('resize', resizeImage);
    return () => {
      window.removeEventListener('resize', resizeImage);
    };
  }, []);

  return (
    <div
      ref={ref}
      id={`preview-${image?.id}`}
      className="preview-item"
      onClick={onClick}
      style={{
        position: 'relative',
        width: notFixedSize ? '100%' : `${placeholderSize?.width}px`,
        height: notFixedSize ? '100%' : `${placeholderSize?.height}px`,
        marginBottom: '30px',
      }}
    >
      {!isLoaded && (
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        }}
        className={`${styles.previewImg} ${styles.previewEmpty}`}
      >
        <Spin
          style={{ width: '100%' }}
          indicator={<LoadingOutlined spin />}
        />
      </div>
      )}
      <img
        src={isLoaded ? image?.url : ''}
        alt=""
        style={{
          opacity: isLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
        }}
        className={styles.previewImg}
        onLoad={(e) => loadedImage(e.currentTarget)}
      />
    </div>
  );
});

// 主组件
const LazyImageGallery: FC<Props> = ({
  data, productId, toJumpIdx, anchor, trigger, showScroll,
}: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [sort, setSort] = useState<number>(Number(searchParams.get('sort') || '0'));
  const initPreviewImages = data.preview_pic
    ? data.preview_pic.map((item, index) => ({ url: item, id: `${index + 1}` }))
    : [];
  const loadingRef = createRef<HTMLDivElement>();
  const [loadMore, setLoadMore] = useState<boolean>(!!sort);
  const [visible, setVisible] = useState<boolean>(false);
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const stopScroll = useRef<boolean>(false);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const [isScrolling, setIsScrolling] = useState(false);

  // eslint-disable-next-line no-multiple-empty-lines, max-len
  const [images, setImages] = useState<ImageType[]>(new Array(data.page_count).fill('').map((_, index) => ({ id: `${index + initPreviewImages.length + 1}`, url: '' })));
  const [loadedIds, setLoadedIds] = useState<Set<string>>(new Set());
  const [previewImages, setPreviewImages] = useState<string[]>(data.preview_pic || []);
  const [firstImageSize, setFirstImageSize] = useState<ImageSize>({
    width: 938,
    height: 528,
  });

  // 获取图片数据
  const getDataSource = async (id: string, pageNo: number, pageSize: number) => {
    let res;
    if (productId) {
      res = await getZoneReportImages(productId, id, pageNo, pageSize);
    } else {
      res = await getReportImages(id, pageNo, pageSize);
    }
    if (res?.length) {
      const list = [...res.slice(5).map((item, index) => ({
        id: `${index + data.preview_pic?.length + 1 || 0}`,
        url: item.images,
      }))];
      setImages(list);
      setPreviewImages(res.map((item) => item.images));
    }
  };

  // 多处理一层
  const handleDataSource = async (paramPageNo: number, paramPageSize: number) => {
    await getDataSource(data?.openid as string, paramPageNo, paramPageSize);
  };

  // 初始化直接获取到所有图片的数据源
  const loadInitial = () => {
    handleDataSource(1, data.page_count);
  };

  useEffect(() => {
    loadInitial();
  }, []);

  useEffect(() => {
    if (loadMore && showScroll) {
      showScroll(true);
    }
  }, [loadMore]);

  // 点击加载更多
  const loadMoreImage = () => {
    setLoadMore(true);
  };

  // 图片加载逻辑
  const handleImageLoad = (id: string) => {
    if (loadedIds.has(id)) return;

    const targetImage = images.find((img) => img.id === id);
    if (!targetImage) return;

    const img = new Image();
    img.src = targetImage.url;
    img.onload = () => {
      setLoadedIds((prev) => new Set([...prev, id]));
    };
  };

  // 跳转到锚点   如果首次触发锚点跳转已触发，再点击相同的anchor 则直接跳转到对应img
  const scrollToAnchorImg = (index: number) => {
    const img = document.getElementById(`preview-${index}`);
    if (img) {
      img.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      if (toJumpIdx) {
        toJumpIdx(index);
      }
      stopScroll.current = false; // 允许滚动
    }
  };

  // 父级传递下来的 锚点跳转参数
  useEffect(() => {
    if (anchor) {
      stopScroll.current = true; // 停止滚动
      if (anchor > 5) {
        setLoadMore(true);
      }
      setTimeout(() => {
        scrollToAnchorImg(anchor);
      }, 200);
      setSort(anchor);
    }
  }, [anchor, trigger]);

  // 图片预览切换处理
  const handlePreviewImageChange = async (index: number) => {
    setPreviewIndex(index);
    if (index >= 5) { // 预览的时候同步加载列表对应的图片
      handleImageLoad(images[index - 5].id);
    }
  };
  // 图片点击放大处理
  const handleImgClick = (item: string, index: number) => {
    previewImages[index] = item;
    setPreviewImages(previewImages);
    setPreviewIndex(index);
    setVisible(true);
  };

  // 定义处理图片加载完成的函数
  const handlePreviewImageLoad = (img: HTMLImageElement) => {
    // 超出会遮挡按钮，则缩小
    if (img.width > (window.innerWidth - 184) || img.height > (window.innerHeight - 170)) {
      // 计算页面宽比
      const widthRatio = (window.innerWidth - 204) / img.width;
      const heightRatio = (window.innerHeight - 180) / img.height;
      const ratio = Math.min(widthRatio, heightRatio);
      // 截取保留一位小数
      const sliceRatio = `${ratio}`.slice(0, 3);
      // eslint-disable-next-line no-param-reassign
      img.style.transform = `scale(${sliceRatio})`;
    }
  };

  // 创建观察器实例(用于监听预览图片的变化)
  const observerPreviewImg = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      // 遍历新增的节点
      if (mutation.previousSibling instanceof HTMLImageElement) {
        const img = mutation.previousSibling;
        // 监听图片加载完成事件
        if (img.complete) {
          handlePreviewImageLoad(img);
        } else {
          img.addEventListener('load', () => handlePreviewImageLoad(img));
        }
      }
    });
  });

  useEffect(() => {
    if (visible) {
      // 开始观察整个文档的 DOM 变化
      observerPreviewImg.observe(document.body, {
        childList: true, // 监听子节点变化
        subtree: true, // 监听所有后代节点
      });
    } else {
      // 关闭预览后界面定位到对应的图片
      if (previewIndex + 1 > 5 && !loadMore) {
        setLoadMore(true);
      }
      setTimeout(() => {
        scrollToAnchorImg(previewIndex + 1);
        if (toJumpIdx) {
          toJumpIdx(previewIndex + 1);
        }
      }, 200);
      observerPreviewImg.disconnect();
    }
  }, [visible]);

  // 计算图片是否在视口内的可见百分比
  const getVisiblePercentage = (el) => {
    const rect = el.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;

    // 计算元素在视口内的可见区域
    const visibleHeight = Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
    const visibleWidth = Math.min(rect.right, windowWidth) - Math.max(rect.left, 0);

    // 如果元素完全不可见，返回 0
    if (visibleHeight <= 0 || visibleWidth <= 0) {
      return 0;
    }

    // 计算元素的总面积
    const totalArea = rect.width * rect.height;

    // 计算可见区域的面积
    const visibleArea = visibleHeight * visibleWidth;

    // 返回可见区域的比例
    return (visibleArea / totalArea) * 100;
  };

  const checkNormalVisible = (node: HTMLElement): boolean => {
    // If this element is hidden by css rules somehow, it's definitely invisible
    if (!(node.offsetWidth || node.offsetHeight || node.getClientRects().length)) {
      return false;
    }

    let top;
    let elementHeight;

    try {
      ({ top, height: elementHeight } = node.getBoundingClientRect());
    } catch (e) {
      ({ top, height: elementHeight } = { top: 0, height: 0 });
    }

    const windowInnerHeight = window.innerHeight || document.documentElement.clientHeight;

    const offsets = 1000;

    return (
      top - offsets <= windowInnerHeight
      && top + elementHeight + offsets >= 0
    );
  };

  // 滚动页面目录跟随处理
  const setScroll = debounce(() => {
    if (stopScroll.current) return; // 锚点跳转的时候不执行

    // 标记为正在滚动
    if (!isScrolling) {
      setIsScrolling(true);
    }
    // 清除之前的定时器
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }

    // 设置新的定时器检测滚动停止
    scrollTimeout.current = setTimeout(() => {
      setIsScrolling(false);
    }, 0); // 没有新滚动事件视为停止

    // 获取滚动容器
    const scrollContainer = document.getElementById('previewBox');
    // 获取所有需要观察的元素
    const items = scrollContainer?.querySelectorAll('.preview-item');
    // 因为在别的页面没有生成ai目录，所以要判断下是否存在toJumpIdx
    if (items && toJumpIdx) {
      items.forEach((item) => {
        const rect = item.getBoundingClientRect();
        const index = item.id.slice(8);
        if (rect.height >= (window.innerHeight || document.documentElement.clientHeight)) {
          // 图片高度超出视图，则判断出现在视图的面积的百分比
          if (getVisiblePercentage(item) > 20) {
            toJumpIdx(Number(index));
          }
        } else {
          // 否则判断元素是否完全出现在视图中
          // eslint-disable-next-line no-lonely-if
          if (rect.top >= 0
            && rect.left >= 0
            && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
            && rect.right <= (window.innerWidth || document.documentElement.clientWidth)) {
            toJumpIdx(Number(index));
          }
        }
      });
    }
  }, 10);

  // 滚动停止后控制isLoaded显示
  useEffect(() => {
    if (loadedIds.size === images.length) return;
    // 如果滚动停止且需要加载更多
    if (!isScrolling && loadMore) {
      const visibleItems = document.querySelectorAll('.preview-item');
      visibleItems.forEach((item) => {
        const id = item.id.slice(8); // 获取图片ID`
        if (checkNormalVisible(item as HTMLElement)) {
          handleImageLoad(id);
        }
      });
    }
  }, [isScrolling]);

  useEffect(() => {
    if (loadMore) {
      handleImageLoad(images[0].id);
    }
  }, [loadMore]);

  // 初始加载监听滚动
  useEffect(() => {
    window.addEventListener('scroll', setScroll);
    return () => {
      window.removeEventListener('scroll', setScroll);
    };
  }, []);

  return (
    <div ref={loadingRef}>
      {!data.preview_pic && (
        <Alert
          message={t('tips')}
          description={t('wait_generating_tips')}
          type="info"
          showIcon
        />
      )}
      {data.preview_pic && (
        <section id="previewBox" className={`${styles.section} ${styles.previewSection}`}>
          {initPreviewImages.map((img, index) => (
            <ImageItem
              image={img}
              index={index}
              key={img.id}
              notFixedSize
              placeholderSize={firstImageSize}
              isLoaded
              sendFirstImageSize={(size) => {
                if (size.width > 0 && size.height > 0) {
                  setFirstImageSize(size);
                }
              }}
              onClick={() => handleImgClick(img.url, index)}
            />
          ))}
          {loadMore && images.map((img, index) => (
            <ImageItem
              image={img}
              index={index}
              key={img.id}
              notFixedSize={false}
              placeholderSize={firstImageSize}
              isLoaded={loadedIds.has(img.id)}
              onClick={() => handleImgClick(img.url, index + 5)}
            />
          ))}
          <PhotoSlider
            images={previewImages.map((item, index) => ({
              src: item, key: item + index,
            }))}
            visible={visible}
            onClose={() => {
              setVisible(false);
            }}
            index={previewIndex}
            onIndexChange={(index: number) => handlePreviewImageChange(index)}
            photoClassName={styles.previewPhoto}
            maskClassName={styles.mask}
            className={styles.photoSliderStyle}
          />
          {!loadMore && data.read_auth === 1 && data.page_count > 5 && (
          <Button
            className={styles.loadingMore}
            onClick={loadMoreImage}
          >
            <div>
              <span>{t('view_full_report')}</span>
              <DownOutlined style={{ fontSize: 10, marginLeft: 6 }} />
            </div>
            <div className={styles.loadingMoreTips}>
              （禁止copy及大范围传播）
            </div>
          </Button>
          )}
        </section>
      )}
    </div>
  );
};

export default LazyImageGallery;
